import isEqual from 'lodash/isEqual';
import { SIDE_PANEL_CONTROL_COMMANDS } from '../../constants';
import type { SidePanelSession } from '../../types';

export type SidePanelSessionState = SidePanelSession;

export type SidePanelState = {
    session: SidePanelSessionState | null;
    isExpanded: boolean;
};

export type SidePanelCommand =
    | { type: typeof SIDE_PANEL_CONTROL_COMMANDS.open; session: SidePanelSessionState }
    | { type: typeof SIDE_PANEL_CONTROL_COMMANDS.toggle; session: SidePanelSessionState }
    | { type: typeof SIDE_PANEL_CONTROL_COMMANDS.close }
    | { type: typeof SIDE_PANEL_CONTROL_COMMANDS.setSubject; subject: unknown }
    | { type: typeof SIDE_PANEL_CONTROL_COMMANDS.patchPayload; payload: Record<string, unknown> }
    | { type: typeof SIDE_PANEL_CONTROL_COMMANDS.setLayout; isExpanded: boolean };

export type SidePanelTransitionResult =
    | { status: 'ok' }
    | { status: 'ignored'; reason: 'alreadyOpen' | 'alreadyClosed' };

export type SidePanelTransition = {
    state: SidePanelState;
    result: SidePanelTransitionResult;
};

export const INITIAL_SIDE_PANEL_STATE: SidePanelState = {
    session: null,
    isExpanded: false
};

export function reduceSidePanelCommand(state: SidePanelState, command: SidePanelCommand): SidePanelTransition {
    switch (command.type) {
        case SIDE_PANEL_CONTROL_COMMANDS.open: {
            const currentSession = state.session;
            const nextSession = command.session;
            const isSameSession =
                currentSession?.panelId === nextSession.panelId &&
                currentSession.ownerId === nextSession.ownerId &&
                isEqual(currentSession.subject, nextSession.subject) &&
                isEqual(currentSession.payload, nextSession.payload);

            if (isSameSession) {
                return { state, result: { status: 'ignored', reason: 'alreadyOpen' } };
            }

            return {
                state: { session: nextSession, isExpanded: false },
                result: { status: 'ok' }
            };
        }
        case SIDE_PANEL_CONTROL_COMMANDS.toggle:
            if (
                state.session?.panelId === command.session.panelId &&
                state.session.ownerId === command.session.ownerId &&
                isEqual(state.session.subject, command.session.subject)
            ) {
                return { state: INITIAL_SIDE_PANEL_STATE, result: { status: 'ok' } };
            }

            return reduceSidePanelCommand(state, { type: SIDE_PANEL_CONTROL_COMMANDS.open, session: command.session });
        case SIDE_PANEL_CONTROL_COMMANDS.close:
            if (!state.session) {
                return { state, result: { status: 'ignored', reason: 'alreadyClosed' } };
            }

            return { state: INITIAL_SIDE_PANEL_STATE, result: { status: 'ok' } };
        case SIDE_PANEL_CONTROL_COMMANDS.setSubject:
            if (!state.session) {
                return { state, result: { status: 'ignored', reason: 'alreadyClosed' } };
            }

            return {
                state: {
                    ...state,
                    session: { ...state.session, subject: command.subject as SidePanelSession['subject'] }
                },
                result: { status: 'ok' }
            };
        case SIDE_PANEL_CONTROL_COMMANDS.patchPayload:
            if (!state.session) {
                return { state, result: { status: 'ignored', reason: 'alreadyClosed' } };
            }

            return {
                state: {
                    ...state,
                    session: { ...state.session, payload: { ...state.session.payload, ...command.payload } }
                },
                result: { status: 'ok' }
            };
        case SIDE_PANEL_CONTROL_COMMANDS.setLayout:
            if (!state.session) {
                return { state, result: { status: 'ignored', reason: 'alreadyClosed' } };
            }

            if (state.isExpanded === command.isExpanded) {
                return { state, result: { status: 'ignored', reason: 'alreadyOpen' } };
            }

            return {
                state: { ...state, isExpanded: command.isExpanded },
                result: { status: 'ok' }
            };
        default: {
            const exhaustiveCheck: never = command;
            return exhaustiveCheck;
        }
    }
}
